<template>
    <div class="dt-pager">
        <a class="btn btn-default btn-lg prev"
           :class="{disabled: isOnFirstPage}"
           @click.prevent="prevPage()">
            <i class="fa-regular fa-angle-left"></i> {{ l('PAGER.PREVIOUS') }}
        </a>

        <div>{{ l('PAGER.CURRENT_PAGE') }} {{ currentPage }} / {{ totalPages }}</div>

        <a class="btn btn-default btn-lg next"
           :class="{disabled: isOnLastPage}"
           @click.prevent="nextPage()">
            {{ l('PAGER.NEXT') }} <i class="fa-regular fa-angle-right"></i>
        </a>
    </div>
</template>

<script>
export default {
    props: {
        // The initial value of 'currentPage'.
        // Use v-model for two-way binding.
        modelValue: {
            type: Number,
            required: true,
        },

        totalItems: {
            type: Number,
            required: true,
        },

        pageSize: {
            type: Number,
            required: true,
        },
    },

    emits: [
        'update:modelValue',
    ],

    data() {
        return {
            scrollTimeout: null,
        };
    },

    computed: {
        currentPage() {
            return this.modelValue;
        },

        totalPages() {
            return this.totalItems ? Math.ceil(this.totalItems / this.pageSize) : 1;
        },

        isOnFirstPage() {
            return this.currentPage <= 1;
        },

        isOnLastPage() {
            return this.currentPage >= this.totalPages;
        },
    },

    methods: {
        prevPage() {
            if (!this.isOnFirstPage) {
                this.setPage(this.currentPage - 1);
            }
        },

        nextPage() {
            if (!this.isOnLastPage) {
                this.setPage(this.currentPage + 1);
            }
        },

        setPage(page) {
            // Emit the page change request so the parent component can do its thing.
            // This component will update its UI when `this.modelValue` changes.
            this.$emit('update:modelValue', page);

            const scrollEl = document.getElementsByTagName('html')[0];
            if (scrollEl) {
                clearTimeout(this.scrollTimeout);
                this.scrollTimeout = setTimeout(() => {
                    scrollEl.scrollTop = 0;
                }, 30);
            }
        },
    },
};
</script>

<style lang="less">
@import (reference) "../../less/variables";

.dt-pager {
    display: flex;
    align-items: center;
    justify-content: center;
    color: @white;
    height: @pager-height;
    width: 100%;

    > a {
        background-color: @btn-default-bg;
        border-color: @btn-default-border;
        color: @btn-default-color;
        font-size: 18px;
        cursor: pointer;
        white-space: nowrap;

        i {
            top: 1px;
        }

        &.prev {
            border-radius: 15px 5px 5px 15px;

            i {
                margin-right: 3px;
            }
        }

        &.next {
            border-radius: 5px 15px 15px 5px;

            i {
                margin-left: 3px;
            }
        }

        &.disabled {
            opacity: .6;
            background-color: @btn-disabled-bg;
            border-color: @btn-disabled-border;
            color: @btn-disabled-color;
            cursor: default;
        }

        &:active,
        &:hover {
            background-color: darken(@btn-default-bg, 4%);
            &.disabled {
                background-color: @btn-disabled-bg;
            }
        }
    }

    > div {
        flex-grow: 1;
        text-align: center;
    }
}

@media (max-width: @screen-xs-max) {
    .dt-pager {
        color: @text-color-mobile;
        height: 39px;

        a {
            font-size: 16px;
            padding: 6px 14px;
            line-height: 24px;
            width: 109px; // Make the prev and next buttons the same width. It's more obvious on mobile
        }
    }

    .footer-btn-bar {
        .dt-pager {
            color: @white;
        }
    }
}
</style>
